import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Title, Text, useMantineTheme, Group, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX, IconMapPin } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useLocations from 'hooks/location/useLocations';
import useUpdateLocation from 'hooks/location/useUpdateLocation';
import { CellProps } from 'react-table';
import { Location } from 'types';
import notificationHandler from 'utils/notifications';

import DeleteLocationModal from './DeleteLocationModal';
import DrawerLocationForm from './DrawerLocationForm';

interface ILocationTableContainerProps {
  searchTerm: string;
  permission?: boolean;
}

const LocationTableContainer = ({
  searchTerm,
  permission = true,
}: ILocationTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);
  const updateLocationMutation = useUpdateLocation();

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useLocations({
      search: searchTerm,
      ordering: ascending ? ordering : `-${ordering}`,
    });
  const [tableData, setTableData] = useState<Location[]>([]);

  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleEdit = (location: Location): void => {
    setSelectedLocation(location);
    openEditForm();
  };

  const handleDelete = (location: Location): void => {
    setSelectedLocation(location);
    openDeleteModal();
  };

  const handleCloseEditForm = (): void => {
    setSelectedLocation(undefined);
    closeEditForm();
  };

  const handleSetDefault = (location: Location): void => {
    const { successNotification, errorNotification } = notificationHandler();
    updateLocationMutation.mutate(
      { ...location, isDefault: true },
      {
        onSuccess: () => {
          successNotification('Location update success', `${location.name} successfully set as default.`);
        },
        onError: error => {
          errorNotification('Location update failed', error.message);
        },
      },
    );
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<Location>) => (
        <Group>
          <Text
            onClick={() => handleEdit(row.original)}
            weight='bold'
            underline
            sx={{ cursor: 'pointer', width: 'fit-content' }}
          >
            {row.original.name}
          </Text>
          {row.original.isDefault && <Badge>Default</Badge>}
        </Group>
      ),
    },
    {
      id: 'action',
      disableSortBy: true,
      Cell: ({ row }: CellProps<Location>) => {
        const items = [
          {
            title: 'Edit',
            icon: <IconEdit size={theme.fontSizes.sm} />,
            onClick: () => handleEdit(row.original),
            'data-test': 'edit-location',
          },
          {
            title: 'Delete',
            icon: <IconTrashX size={theme.fontSizes.sm} />,
            color: 'red',
            onClick: () => handleDelete(row.original),
            'data-test': 'delete-location',
          },
        ];

        if (!row.original.isDefault) {
          items.push({
            title: 'Set As Default',
            icon: <IconMapPin size={theme.fontSizes.sm} />,
            onClick: () => handleSetDefault(row.original),
            'data-test': 'set-default-location',
          });
        }

        return (
          <Group position='right' px='xs'>
            <Menu position='bottom-end' items={items} disabled={!permission} />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
      />
      {selectedLocation && (
        <Modal
          opened={isDeleteModalOpen}
          onClose={closeDeleteModal}
          title={<Title order={4}>Delete Location</Title>}
          trapFocus
          centered
          size='auto'
        >
          <DeleteLocationModal location={selectedLocation} handleClose={closeDeleteModal} />
        </Modal>
      )}
      <DrawerLocationForm
        formId='edit-location-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Location</Title>}
        opened={isEditFormOpen}
        locationId={selectedLocation?.locationId}
        closeHandler={handleCloseEditForm}
        type='edit'
        permission={permission}
      />
    </>
  );
};

export default LocationTableContainer;
